import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Button,
  IconButton,
  Box,
  Typography,
  Chip
} from "@material-ui/core";
import {
  Mic as MicIcon,
  Stop as StopIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  Check as CheckIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import MicRecorder from "mic-recorder-to-mp3";
import { toast } from "react-toastify";
import getAudioDurationSeconds from "../../utils/getAudioDurationSeconds";

const MIN_RECORDING_BYTES = 10000;

const useStyles = makeStyles((theme) => ({
  recorderContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(2),
    border: `1px dashed ${theme.palette.grey[400]}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2)
  },
  recordingControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  recordButton: {
    backgroundColor: "#f44336",
    color: "white",
    "&:hover": {
      backgroundColor: "#d32f2f"
    }
  },
  recordingIndicator: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  timeDisplay: {
    fontFamily: "monospace",
    fontSize: "1.2rem",
    color: theme.palette.text.primary
  },
  audioControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%"
  },
  previewBar: {
    width: "100%",
    minHeight: 48,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[200],
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(1)
  }
}));

const AudioRecorder = ({ onAudioRecorded, onAudioDeleted, disabled = false }) => {
  const classes = useStyles();
  const recorderRef = useRef(null);
  const getRecorder = useCallback(() => {
    if (!recorderRef.current) {
      recorderRef.current = new MicRecorder({
        bitRate: 128,
        sampleRate: 44100
      });
    }
    return recorderRef.current;
  }, []);

  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);

  const audioRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const resetRecorderAfterStop = useCallback(async () => {
    try {
      await getRecorder().stop().getMp3();
    } catch (_) {
      /* ignora se não estava gravando */
    }
  }, [getRecorder]);

  const startRecording = async () => {
    try {
      await resetRecorderAfterStop();
      await getRecorder().start();
      setIsRecording(true);
      setRecordingTime(0);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Erro ao iniciar gravação:", error);
      toast.error("Erro ao acessar o microfone. Verifique as permissões.");
    }
  };

  const stopRecording = async () => {
    if (!isRecording) return;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
    try {
      const [, blob] = await getRecorder().stop().getMp3();
      if (!blob || blob.size < MIN_RECORDING_BYTES) {
        toast.warn("Gravação muito curta. Segure o microfone um pouco mais.");
        setRecordingTime(0);
        await resetRecorderAfterStop();
        return;
      }
      const seconds = await getAudioDurationSeconds(blob);
      setDurationSeconds(seconds);
      setAudioBlob(blob);
      const file = new File([blob], `audio_${Date.now()}.mp3`, {
        type: blob.type || "audio/mpeg"
      });
      const url = URL.createObjectURL(file);
      setAudioUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (error) {
      console.error("Erro ao finalizar gravação:", error);
      toast.error("Erro ao processar o áudio gravado.");
      setRecordingTime(0);
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const deleteRecording = async () => {
    setAudioBlob(null);
    setRecordingTime(0);
    setDurationSeconds(0);
    setIsPlaying(false);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    await resetRecorderAfterStop();
    if (onAudioDeleted) {
      onAudioDeleted();
    }
  };

  const confirmRecording = () => {
    if (audioBlob && onAudioRecorded) {
      onAudioRecorded(audioBlob);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <Box className={classes.recorderContainer}>
      {!audioBlob && !isRecording && (
        <Button
          variant="contained"
          className={classes.recordButton}
          startIcon={<MicIcon />}
          onClick={startRecording}
          disabled={disabled}
          size="large"
        >
          Gravar Áudio
        </Button>
      )}

      {isRecording && (
        <Box className={classes.recordingIndicator}>
          <Chip
            icon={<MicIcon />}
            label="Gravando..."
            color="secondary"
            variant="outlined"
          />
          <Typography className={classes.timeDisplay}>
            {formatTime(recordingTime)}
          </Typography>
          <IconButton onClick={stopRecording} color="primary">
            <StopIcon />
          </IconButton>
        </Box>
      )}

      {audioBlob && (
        <>
          <Box className={classes.previewBar}>
            <Typography variant="body2" color="textSecondary">
              MP3 — {formatTime(durationSeconds || recordingTime)}
            </Typography>
          </Box>

          <audio
            ref={audioRef}
            src={audioUrl || undefined}
            onEnded={() => setIsPlaying(false)}
            style={{ display: "none" }}
          />

          <Box className={classes.audioControls}>
            <IconButton
              onClick={isPlaying ? pauseAudio : playAudio}
              color="primary"
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </IconButton>

            <Box style={{ flexGrow: 1 }}>
              <Typography variant="body2" align="center">
                Clique em ✓ para confirmar ou 🗑️ para excluir
              </Typography>
            </Box>

            <IconButton onClick={deleteRecording} color="secondary">
              <DeleteIcon />
            </IconButton>

            <IconButton onClick={confirmRecording} color="primary">
              <CheckIcon />
            </IconButton>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AudioRecorder;
